// CertificatesPanel — issue Transfer, Bonafide and Character certificates
// (pre-filled from the student record), keep a numbered register, reprint.
// Everything printed is saved in `details`, so a reprint matches the original.
import { useEffect, useRef, useState } from 'react';
import { FileBadge, Printer } from 'lucide-react';
import { getCertificates, getCertificatePrefill, issueCertificate, getStudentFees } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';
import { Field, Msg, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import PrintSheet from '../ui/PrintSheet';
import StudentPicker from './StudentPicker';
import { fmtDate, todayISO, dateInWords, inr } from '../ui/format';

const TYPES = {
  tc:        { label: 'Transfer Certificate', short: 'TC' },
  bonafide:  { label: 'Bonafide Certificate', short: 'BC' },
  character: { label: 'Character Certificate', short: 'CC' },
};

export default function CertificatesPanel({ initialStudentId, onOpenStudent }) {
  const [student,  setStudent]  = useState(null);
  const [type,     setType]     = useState('bonafide');
  const [pre,      setPre]      = useState(null);
  const [form,     setForm]     = useState(null);
  const [markLeft, setMarkLeft] = useState(true);
  const [register, setRegister] = useState([]);
  const [regType,  setRegType]  = useState('');
  const [printing, setPrinting] = useState(null);  // certificate row
  const [msg,      setMsg]      = useState('');
  const [busy,     setBusy]     = useState(false);

  useEffect(() => { loadRegister(); }, [regType]);
  useEffect(() => { if (initialStudentId) setStudent({ id: initialStudentId }); }, [initialStudentId]);
  // Clear the previous student's form straight away — otherwise their details
  // would stay on screen (and could be issued) until the new student loads.
  const selectedId = useRef(null);
  useEffect(() => { selectedId.current = student?.id ?? null; setPre(null); setForm(null); setMsg(''); if (student?.id) loadPrefill(student.id); }, [student?.id]);
  useEffect(() => { if (pre) setForm(defaults(type, pre)); }, [type, pre]);

  async function loadRegister() { try { setRegister(await getCertificates(regType ? { type: regType } : {})); } catch (err) { setMsg(errText(err)); } }
  async function loadPrefill(id) {
    try {
      const [p, fees] = await Promise.all([getCertificatePrefill(id), getStudentFees(id).catch(() => null)]);
      p.dues = fees?.configured ? fees.amount_due : 0;
      if (selectedId.current !== id) return; // a late reply for a student no longer selected
      setPre(p);
      if (!student.name) setStudent({ id, name: p.student.name, class_name: p.student.class_name });
    } catch (err) { setMsg(errText(err)); }
  }

  async function issue() {
    setBusy(true); setMsg('');
    try {
      const details = { ...snapshot(pre), ...form };
      const r = await issueCertificate({ student_id: pre.student.id, cert_type: type, issued_on: form.issued_on, details,
        mark_left: type === 'tc' && pre.student.status === 'active' && markLeft });
      setMsg(`${TYPES[type].label} No. ${r.serial_no} issued ✅${type === 'tc' && markLeft && pre.student.status === 'active' ? ` · ${pre.student.name} is now marked as left` : ''}`);
      setPrinting(r); loadRegister(); loadPrefill(pre.student.id);
    } catch (err) { setMsg(errText(err)); }
    finally { setBusy(false); }
  }

  const s = pre?.student;
  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Certificates</h2>
      <Msg msg={msg} className="mb-3" />

      <div className="grid grid-cols-[minmax(320px,460px)_1fr] gap-5 items-start max-[1100px]:grid-cols-1">
        <Card className="p-5 flex flex-col gap-3">
          <h3 className="font-display font-bold text-[15px] text-ink">Issue a certificate</h3>
          <div className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Student
            <StudentPicker value={student?.name ? student : null} onChange={setStudent} />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {Object.entries(TYPES).map(([k, t]) => (
              <button key={k} onClick={() => setType(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border ${type === k ? 'bg-transit text-white border-transit' : 'border-line text-ink'}`}>{t.label}</button>
            ))}
          </div>
          {s && form && (
            <>
              <p className="text-sm text-ink-soft">
                <b className="text-ink">{s.name}</b> · {s.last_class ? `Class ${s.last_class}` : 'No class'} · {s.status === 'active' ? 'Studying' : s.status === 'alumni' ? 'Alumni' : 'Left'}
                <span className="block text-xs">Next number: {TYPES[type].short}/{pre.next_serial[type]}</span>
              </p>
              <CertForm type={type} form={form} setForm={setForm} pre={pre} />
              {type === 'tc' && s.status === 'active' && (
                <label className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <input type="checkbox" checked={markLeft} onChange={e => setMarkLeft(e.target.checked)} className="accent-[var(--transit)]" />
                  Mark {s.name} as left the school (removes from class, bus and new fee months)
                </label>
              )}
              {type === 'tc' && pre.dues > 0 && (
                <p className="text-sm font-semibold text-critical bg-critical-tint px-3 py-2 rounded-s">Fee dues of {inr(pre.dues)} are outstanding.</p>
              )}
              <div className="flex gap-2">
                <Button onClick={() => setPrinting({ cert_type: type, serial_no: pre.next_serial[type], issued_on: form.issued_on, details: { ...snapshot(pre), ...form }, preview: true })} variant="secondary">Preview</Button>
                <Button onClick={issue} disabled={busy} className="flex items-center gap-1.5"><FileBadge size={14} /> {busy ? 'Issuing…' : 'Issue & print'}</Button>
              </div>
            </>
          )}
          {!student && <p className="text-sm text-ink-soft">Search for a student (alumni and students who left are included).</p>}
        </Card>

        <Card>
          <CardHeader title={`Register (${register.length})`} action={
            <select className="border border-line rounded-s px-2 py-1.5 text-sm bg-paper-raised" value={regType} onChange={e => setRegType(e.target.value)} aria-label="Type">
              <option value="">All types</option>{Object.entries(TYPES).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
            </select>
          } />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className={theadRowCls}><th className="px-4 py-2.5">No.</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Student</th><th className="px-4 py-2.5">Issued</th><th className="px-4 py-2.5"></th></tr></thead>
              <tbody>
                {register.map(c => (
                  <tr key={c.id} className="border-t border-line-soft">
                    <td className="px-4 py-3 font-bold tabular-nums">{TYPES[c.cert_type].short}/{c.serial_no}</td>
                    <td className="px-4 py-3">{TYPES[c.cert_type].label}</td>
                    <td className="px-4 py-3"><button onClick={() => onOpenStudent(c.student_id)} className="font-semibold text-transit-deep hover:underline">{c.student_name}</button>
                      {c.student_status !== 'active' && <span className="ml-1.5"><Badge tone="neutral">{c.student_status}</Badge></span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(c.issued_on)}</td>
                    <td className="px-4 py-3 text-right"><Button variant="secondary" className="!px-2.5 !py-1 !text-xs inline-flex items-center gap-1" onClick={() => setPrinting(c)}><Printer size={12} /> Reprint</Button></td>
                  </tr>
                ))}
                {!register.length && <EmptyRow cols={5}>No certificates issued yet</EmptyRow>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {printing && (
        <PrintSheet title={`${TYPES[printing.cert_type].label}${printing.preview ? ' — preview (not issued)' : ''}`} onClose={() => setPrinting(null)}>
          <CertificatePrint cert={printing} />
        </PrintSheet>
      )}
    </div>
  );
}

// Fields frozen into the certificate at issue time.
function snapshot(pre) {
  const s = pre.student;
  return {
    school: pre.school, session: pre.session?.name || null,
    name: s.name, father_name: s.father_name, mother_name: s.mother_name, guardian_name: s.guardian_name,
    gender: s.gender, category: s.category, admission_no: s.admission_no, date_of_birth: s.date_of_birth,
    date_of_admission: s.date_of_admission, class_name: s.last_class, address: s.address,
  };
}

function defaults(type, pre) {
  const s = pre.student;
  const base = { issued_on: todayISO() };
  if (type === 'bonafide') return { ...base, purpose: '' };
  if (type === 'character') return { ...base, conduct: 'Good', from_date: s.date_of_admission || '', to_date: s.date_of_leaving || '' };
  return {
    ...base, nationality: 'Indian', date_of_leaving: s.date_of_leaving || todayISO(),
    qualified: pre.last_result?.result === 'promoted' ? 'Yes' : pre.last_result?.result === 'detained' ? 'No' : '',
    working_days: pre.attendance?.marked ? String(pre.attendance.marked) : '', days_present: pre.attendance?.marked ? String(pre.attendance.present) : '',
    conduct: 'Good', reason: '', dues: pre.dues > 0 ? `${inr(pre.dues)} outstanding` : 'No dues', remarks: '',
  };
}

function CertForm({ type, form, setForm, pre }) {
  const f = (key, label, props = {}) => (
    <Field label={label} className={props.wide ? 'col-span-2' : ''}>
      <input className={inputCls} value={form[key] ?? ''} onChange={e => setForm({ ...form, [key]: e.target.value })} {...props.input} />
    </Field>
  );
  return (
    <div className="grid grid-cols-2 gap-3">
      {f('issued_on', 'Date of issue', { input: { type: 'date' } })}
      {type === 'bonafide' && f('purpose', 'Purpose', { wide: true, input: { placeholder: 'e.g. opening a bank account, scholarship', required: true } })}
      {type === 'character' && <>
        {f('conduct', 'Conduct & character')}
        {f('from_date', 'Studied from', { input: { type: 'date' } })}
        {f('to_date', 'Studied till (blank = still studying)', { input: { type: 'date' }, wide: true })}
      </>}
      {type === 'tc' && <>
        {f('date_of_leaving', 'Date of leaving', { input: { type: 'date' } })}
        {f('nationality', 'Nationality')}
        {f('qualified', 'Qualified for promotion?', { input: { placeholder: 'Yes / No' } })}
        {f('conduct', 'General conduct')}
        {f('working_days', 'Working days (this year)')}
        {f('days_present', 'Days present')}
        {f('reason', 'Reason for leaving', { wide: true, input: { placeholder: "e.g. Parent's transfer" } })}
        {f('dues', 'Fee dues', { wide: true })}
        {f('remarks', 'Other remarks', { wide: true })}
      </>}
      {!pre.student.father_name && <p className="col-span-2 text-xs text-caution">Father's name isn't on the student record — add it on the Students page for a complete certificate.</p>}
    </div>
  );
}

// ── Printed layouts ─────────────────────────────────────────
function pron(gender) {
  if (gender === 'Male') return { he: 'He', his: 'his', him: 'him', child: 'son' };
  if (gender === 'Female') return { he: 'She', his: 'her', him: 'her', child: 'daughter' };
  return { he: 'He/She', his: 'his/her', him: 'him/her', child: 'son/daughter' };
}

export function CertificatePrint({ cert }) {
  const d = cert.details || {};
  const t = TYPES[cert.cert_type];
  const p = pron(d.gender);
  const parents = [d.father_name && `Shri ${d.father_name}`, d.mother_name && `Smt. ${d.mother_name}`].filter(Boolean).join(' and ') || d.guardian_name || '—';
  const school = d.school || {};
  return (
    <div className="text-[#101b22] font-body" style={{ fontSize: '14px', lineHeight: 1.6 }}>
      <div className="text-center border-b-2 border-[#101b22] pb-3 mb-4">
        <div className="font-display font-extrabold text-2xl">{school.name}</div>
        <div className="text-[13px] text-[#405058]">{[school.location, school.phone && `Ph. ${school.phone}`, school.contact_email].filter(Boolean).join(' · ')}</div>
      </div>
      <div className="flex justify-between text-[13px] mb-3">
        <span>No. <b>{t.short}/{cert.serial_no}</b></span>
        <span>Date: <b>{fmtDate(cert.issued_on)}</b></span>
      </div>
      <h2 className="text-center font-display font-extrabold text-lg uppercase tracking-[0.15em] underline underline-offset-4 mb-5">{t.label}</h2>

      {cert.cert_type === 'bonafide' && (
        <p className="text-justify">
          This is to certify that <b>{d.name}</b>, {p.child} of <b>{parents}</b>{d.admission_no ? <>, Admission No. <b>{d.admission_no}</b></> : null},
          is a bonafide student of this school, studying in <b>Class {d.class_name || '—'}</b>{d.session ? <> during the academic session <b>{d.session}</b></> : null}.
          {d.date_of_birth ? <> {p.his.charAt(0).toUpperCase() + p.his.slice(1)} date of birth as per school records is <b>{fmtDate(d.date_of_birth)}</b> ({dateInWords(d.date_of_birth)}).</> : null}
          {d.purpose ? <> This certificate is issued on {p.his} request for the purpose of <b>{d.purpose}</b>.</> : null}
        </p>
      )}

      {cert.cert_type === 'character' && (
        <p className="text-justify">
          This is to certify that <b>{d.name}</b>, {p.child} of <b>{parents}</b>{d.admission_no ? <>, Admission No. <b>{d.admission_no}</b></> : null},
          {d.to_date ? <> was a student of this school from <b>{fmtDate(d.from_date)}</b> to <b>{fmtDate(d.to_date)}</b></> : <> has been a student of this school since <b>{fmtDate(d.from_date)}</b></>}
          {d.class_name ? <>, {d.to_date ? 'last studying' : 'presently studying'} in <b>Class {d.class_name}</b></> : null}.
          {' '}During this period {p.his} conduct and character have been <b>{d.conduct || 'good'}</b>. To the best of our knowledge {p.he.toLowerCase()} bears a good moral character.
          {' '}We wish {p.him} every success in life.
        </p>
      )}

      {cert.cert_type === 'tc' && (
        <table className="w-full text-[13.5px] border-collapse">
          <tbody>
            {[
              ['Name of the pupil', d.name],
              ["Father's name", d.father_name],
              ["Mother's name", d.mother_name],
              ['Nationality', d.nationality],
              ['Category', d.category],
              ['Date of birth (as per admission register)', d.date_of_birth ? `${fmtDate(d.date_of_birth)} (${dateInWords(d.date_of_birth)})` : null],
              ['Admission number & date of admission', [d.admission_no, d.date_of_admission && fmtDate(d.date_of_admission)].filter(Boolean).join(' · ')],
              ['Class in which the pupil last studied', d.class_name],
              ['Whether qualified for promotion to the next class', d.qualified],
              ['Total working days / days present', d.working_days ? `${d.working_days} / ${d.days_present || '—'}` : null],
              ['General conduct', d.conduct],
              ['Date on which the pupil left the school', d.date_of_leaving && fmtDate(d.date_of_leaving)],
              ['Reason for leaving the school', d.reason],
              ['Fee dues, if any', d.dues],
              ['Any other remarks', d.remarks],
            ].map(([label, value], i) => (
              <tr key={label}><td className="w-8 align-top">{i + 1}.</td><td className="align-top">{label}</td><td className="align-top font-bold">{value || '—'}</td></tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex justify-between mt-16 text-xs text-[#405058]">
        {cert.cert_type === 'tc' && <div className="border-t border-[#101b22] pt-1 px-6">Class teacher</div>}
        {cert.cert_type === 'tc' && <div className="border-t border-[#101b22] pt-1 px-6">Checked by</div>}
        <div className={`border-t border-[#101b22] pt-1 px-6 ${cert.cert_type !== 'tc' ? 'ml-auto' : ''}`}>Principal (signature &amp; seal)</div>
      </div>
    </div>
  );
}
